import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChainFees, TransactionFee, FooterItem } from '../../../config/feeConfig/feeConfig';
import { FlowConfigContext } from '../../../providers/FlowConfigContext';
import { GatekeeperNetworkServiceContext } from '../../../providers/GatekeeperNetworkService/GatekeeperNetworkServiceContext';
import FeeTabs from './FeeTabs';
import SVGQuestion from '../../assets/svg/icons/SVGQuestion';
import { Expiry } from '../../../providers/GatekeeperNetworkService/GatekeeperNetworkServiceData';
import { getGknValue } from '../../../providers/GatekeeperNetworkService/utils';
import { Action } from '../../../config';
import Loading from '../Loading/Loading';
import { useFees } from '../../../providers/FeesContext';

export default function FeePanel({ fundsCheckMode = false }: { fundsCheckMode?: boolean }): React.ReactElement {
  const { networkName, action } = useContext(FlowConfigContext);
  const { showFeesAtStart, gknServiceData, requestComplete, fetchGatekeeperNetwork } = useContext(GatekeeperNetworkServiceContext);
  const { t } = useTranslation();
  const [feeDisplayType, setFeeDisplayType] = useState<'crypto' | 'usd'>('crypto');
  const [feesDisplay, setFeesDisplay] = useState<ChainFees | undefined>(undefined);

  const { walletCryptoBalance, walletBalanceDetail, cryptoCurrencyCode, fees } = useFees();
  const expiry = getGknValue<Expiry>(gknServiceData!, 'expiry');

  const getExpirationInDays = () => {
    if (expiry?.maxRefreshPeriodSeconds) {
      return Math.floor(expiry.maxRefreshPeriodSeconds / 86400);
    }

    const expirationInSeconds = action === Action.REFRESH ? expiry?.extendExpirySeconds : expiry?.initialExpirySeconds;

    if (!expirationInSeconds) {
      return 30; // Default to 30 days if no expiration is set
    }

    return Math.floor(expirationInSeconds / 86400);
  };

  useEffect(() => {
    if (feeDisplayType === 'crypto') {
      setFeesDisplay(fees?.crypto);
    } else {
      setFeesDisplay(fees?.usd);
    }
  }, [fees, feeDisplayType]);

  const onFeesInfoClick = () => {
    window.open(
      'https://support.civic.com/hc/en-us/articles/16584956659095-What-are-the-fees-for-getting-a-Civic-Pass-',
      '_blank',
    );
  };

  if (!requestComplete) {
    throw fetchGatekeeperNetwork;
  }

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {(!walletBalanceDetail || walletBalanceDetail?.hasSufficientFunds) && fundsCheckMode && !showFeesAtStart ? (
        <div className="flex justify-center items-center w-full h-16">
          <Loading key="loading" />
        </div>
      ) : (
        <>
          <div className="-mt-2 w-full flex justify-center items-center font-extralight text-sm">
            <div
              role="button"
              tabIndex={0}
              className="cursor-pointer flex place-items-center"
              onClick={onFeesInfoClick}
              onKeyDown={(e) => {
                // Activate on 'Enter' or 'Space' key press
                if (e.key === 'Enter' || e.key === ' ') {
                  onFeesInfoClick();
                }
              }}
            >
              {cryptoCurrencyCode
                ? t('common.fees.subtitleWithCurrency', { currency: cryptoCurrencyCode })
                : t('common.fees.subtitle')}
              <div className="ml-1">
                <SVGQuestion />
              </div>
            </div>
          </div>
          <div className="text-textParagraph mt-4 pb-4 px-4 w-full">
            {feesDisplay && (
              <div className="flex w-full justify-end mb-4">
                <FeeTabs baseCurrency="USD" cryptoCurrency={cryptoCurrencyCode} setFeeDisplayType={setFeeDisplayType} />
              </div>
            )}
            {feesDisplay ? (
              <>
                {feesDisplay?.transactionFees?.map((fee: TransactionFee) => {
                  return (
                    <div
                      className="w-full grid gap-2 grid-cols-10 mb-1"
                      key={fee.nameI18n}
                      data-testid="verificationLine"
                    >
                      <span className="text-left font-extralight col-span-6" data-testid="gasFee">
                        {t(fee.nameI18n, { context: networkName, days: getExpirationInDays() })}
                        <span className="text-xs align-top ml-0.5">{fee.footerNumber}</span>
                      </span>
                      {fee.loading || false ? (
                        <div className="relative flex justify-end h-5 items-center pr-4 col-span-4">
                          <div className="small-loading-spinner" />
                        </div>
                      ) : (
                        <span className="text-right font-extralight col-span-4" data-testid="displayAmount">
                          {fee.displayAmount}
                        </span>
                      )}
                    </div>
                  );
                })}

                <div className="w-full grid gap-2 grid-cols-3 font-bold">
                  <span className="text-left col-span-1" data-testid="transactionTotal">
                    {t('common.fees.transactionTotal', { context: networkName })}
                  </span>
                  {feesDisplay.total === undefined ? (
                    <div className="relative flex justify-end h-5 items-center pr-4 col-span-2">
                      <div className="small-loading-spinner" />
                    </div>
                  ) : (
                    <span className="text-right col-span-2" data-testid="gasPlusFees">
                      {feesDisplay.total} {feesDisplay.currency}
                    </span>
                  )}
                </div>

                {fundsCheckMode && (
                  <div className="w-full mt-2 grid gap-2 grid-cols-3 font-bold text-textHyperlink">
                    <span className="text-left col-span-1" data-testid="transactionTotal">
                      {t('common.balance.yourWalletBalance')}
                    </span>
                    {walletCryptoBalance?.status === 'loading' ? (
                      <div className="relative flex justify-end h-5 items-center pr-4 col-span-4">
                        <div className="small-loading-spinner" />
                      </div>
                    ) : (
                      <span className="text-right col-span-2" data-testid="gasPlusFees">
                        {feeDisplayType === 'crypto' ? walletBalanceDetail?.crypto : walletBalanceDetail?.usd}{' '}
                        {feesDisplay.currencyOnly}
                      </span>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="flex-auto text-lg text-center leading-6 text-textParagraph mb-2">
                {t('common.fees.transactionMissing', { context: networkName })}
              </div>
            )}
          </div>

          <div className="full-width-ruler mb-2" />

          <div className="text-textHyperlink font-extralight px-4 w-full mb-2" data-testid="footnotes">
            {feesDisplay && feesDisplay?.footers ? (
              feesDisplay?.footers?.map((footer: FooterItem) => {
                return (
                  <div className="w-full mb-1 text-left flex flex-row" key={footer.nameI18n}>
                    <span className="text-xs text-textTertiary align-top mr-1">{footer.number}</span>
                    <span className="text-sm text-textTertiary">
                      {t(footer.nameI18n, { context: networkName, days: getExpirationInDays() })}
                    </span>
                  </div>
                );
              })
            ) : (
              <div className="flex-auto text-md text-center text-textTertiary leading-6 mb-2">
                {t('common.fees.footerDefault', { context: networkName, days: getExpirationInDays() })}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}

FeePanel.defaultProps = {
  fundsCheckMode: false,
};
